import { Box, Tooltip } from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { faTag } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';

import useControllers from './useControllers';
import CanadianBrandNoticeModal from '../CanadianBrandNoticeModal';
import { BrandDetailsFormValues, ValidationRule } from '../../types';
import { ENTITY_TYPES, OPEN_CANADIAN_BRAND_NOTICE } from '@/constants';
import { EntityTypes } from '@/shared_elements/enums';
import { IntegrationReactSelect } from '@/ui_elements';
import { SelectItem } from '@/utils/types';

interface BrandDetailsFormProps {
  countryOptions?: SelectItem[];
  usStateOptions?: SelectItem[];
  stockExchangeOptions?: SelectItem[];
  verticalTypeOptions?: SelectItem[];
  validationRules?: ValidationRule[];
  defaultValues?: BrandDetailsFormValues;
  onNext?: (values: BrandDetailsFormValues) => void;
}

enum ModalType {
  None = 'NONE',
  Notice = 'NOTICE',
}

const BrandDetailsForm: FunctionComponent<BrandDetailsFormProps> = ({
  countryOptions = [],
  usStateOptions = [],
  stockExchangeOptions = [],
  verticalTypeOptions = [],
  validationRules = [],
  defaultValues,
  onNext,
}) => {
  const {
    control,
    formState: { isSubmitting, errors },
    clearErrors,
    handleSubmit,
  } = useForm<BrandDetailsFormValues>({
    mode: 'onTouched',
    defaultValues,
  });
  const {
    companyName,
    setCompanyName,
    validateCompanyName,
    companyNameDisabled,
    companyNameError,
    displayName,
    setDisplayName,
    validateDisplayName,
    displayNameError,
    entityType,
    setEntityType,
    entityTypeError,
    country,
    setCountry,
    countryError,
    ein,
    setEin,
    validateEin,
    einDisabled,
    einError,
    einIssuingCountry,
    setEinIssuingCountry,
    einIssuingCountryDisabled,
    einIssuingCountryError,
    altBusinessIdType,
    setAltBusinessIdType,
    altBusinessIdTypeDisabled,
    altBusinessIdTypeError,
    altBusinessId,
    setAltBusinessId,
    validateAltBusinessId,
    altBusinessIdDisabled,
    altBusinessIdError,
    street,
    setStreet,
    validateStreet,
    streetDisabled,
    streetError,
    city,
    setCity,
    validateCity,
    cityDisabled,
    cityError,
    state,
    setState,
    validateState,
    stateDisabled,
    stateError,
    postalCode,
    setPostalCode,
    validatePostalCode,
    postalCodeDisabled,
    postalCodeError,
    website,
    setWebsite,
    validateWebsite,
    websiteDisabled,
    websiteError,
    stockSymbol,
    setStockSymbol,
    validateStockSymbol,
    stockSymbolDisabled,
    stockSymbolError,
    stockExchange,
    setStockExchange,
    stockExchangeDisabled,
    stockExchangeError,
    verticalType,
    setVerticalType,
    verticalTypeDisabled,
    verticalTypeError,
    referenceId,
    setReferenceId,
    validateReferenceId,
    referenceIdDisabled,
    referenceIdError,
    firstName,
    setFirstName,
    validateFirstName,
    firstNameDisabled,
    firstNameError,
    lastName,
    setLastName,
    validateLastName,
    lastNameDisabled,
    lastNameError,
    mobilePhone,
    setMobilePhone,
    validateMobilePhone,
    mobilePhoneDisabled,
    mobilePhoneError,
  } = useControllers(control, validationRules);
  // TCR-17741 Only trigger side effects for value change after mounted
  const [mounted, setMounted] = useState(false);
  const [noticeShown, setNoticeShown] = useState(
    !!localStorage.getItem(OPEN_CANADIAN_BRAND_NOTICE)
  );
  const [modalType, setModalType] = useState(ModalType.None);

  const localizedCountryOptions = useMemo(() => {
    const options =
      entityType === EntityTypes.Government
        ? []
        : countryOptions.filter(
            (option) => !['US', 'CU', 'IR', 'KP', 'SY'].includes(option.value)
          );
    options.unshift({ label: 'United States', value: 'US' });
    return options;
  }, [countryOptions, entityType]);
  const selectedCountryOption = useMemo(
    () => localizedCountryOptions.find((option) => option.value === country),
    [localizedCountryOptions, country]
  );
  const selectedEinIssuingCountryOption = useMemo(
    () =>
      localizedCountryOptions.find(
        (option) => option.value === einIssuingCountry
      ),
    [localizedCountryOptions, einIssuingCountry]
  );
  const selectedStateOption = useMemo(
    () => usStateOptions.find((option) => option.value === state),
    [usStateOptions, state]
  );
  const selectedStockExchangeOption = useMemo(
    () => stockExchangeOptions.find((option) => option.value === stockExchange),
    [stockExchangeOptions, stockExchange]
  );
  const selectedVerticalTypeOption = useMemo(
    () => verticalTypeOptions.find((option) => option.value === verticalType),
    [verticalTypeOptions, verticalType]
  );

  const handleValid = (values: BrandDetailsFormValues) => {
    onNext?.(values);
  };

  useEffect(() => {
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted || entityType === EntityTypes.SoleProprietor) {
      return;
    }

    setDisplayName(companyName);
  }, [companyName]);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    if (entityType === EntityTypes.SoleProprietor) {
      setCompanyName('');
      setEin('');
      setEinIssuingCountry('');
      setAltBusinessIdType('');
      setAltBusinessId('');
      clearErrors('vertical');
    } else {
      setFirstName('');
      setLastName('');
      setMobilePhone('');
      clearErrors('referenceId');
    }
    if (entityType === EntityTypes.Government) {
      setCountry('US');
    }
    if (entityType !== EntityTypes.PubliclyTradedCompany) {
      setStockSymbol('');
      setStockExchange('');
      clearErrors('website');
    }
  }, [entityType]);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    setEin('');
    setEinIssuingCountry(
      entityType === EntityTypes.SoleProprietor ? '' : country
    );
    setStreet('');
    setCity('');
    setState('');
    setPostalCode('');
  }, [country]);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    clearErrors('ein');
  }, [einIssuingCountry]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <Grid
        className="csp-portal"
        container
        justifyContent="flex-start"
        spacing={0}
      >
        <Grid item lg={12}>
          <div className="csp-verification-form brands">
            <div className="csp-details basic">
              <div className="csp-details-heading">
                <Box flexDirection="row" alignItems="center">
                  <Box margin={{ right: 'xs' }}>
                    <FontAwesomeIcon icon={faTag} size="xl" />
                  </Box>
                  <h3 className="heading1">Brand Details</h3>
                </Box>
              </div>
              <Grid container spacing={4} data-testid="brandBasicDetailsForm">
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormCompanyNameInput"
                      id="companyName"
                      inputProps={{
                        'aria-label': 'companyName',
                        autoComplete: 'new-password',
                        form: {
                          autoComplete: 'off',
                        },
                      }}
                      fullWidth
                      required={entityType !== EntityTypes.SoleProprietor}
                      disabled={companyNameDisabled}
                      autoComplete="off"
                      label="Brand Legal Name"
                      value={companyName}
                      error={!!companyNameError?.message}
                      onChange={(event) => setCompanyName(event.target.value)}
                      onBlur={validateCompanyName}
                    />
                    <Tooltip
                      title="For Brand Legal Name, refer to your official
                      letter from the IRS (CP-575). Enter the text from NAME
                      LINE 1 as your Brand Legal Name. In some instances, you
                      may also need to append the text from NAME LINE 2. The
                      only special characters allowed are hyphens (-) and
                      ampersands (&)."
                    />
                    {companyNameError?.message && (
                      <h6 className="error-msg">{companyNameError.message}</h6>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormDisplayNameInput"
                      id="displayName"
                      inputProps={{ 'aria-label': 'displayName' }}
                      fullWidth
                      required
                      label="Brand DBA Name, if different from legal name"
                      value={displayName}
                      error={!!displayNameError?.message}
                      onChange={(event) => setDisplayName(event.target.value)}
                      onBlur={validateDisplayName}
                    />
                    <Tooltip title="Type in the 'Doing Business As' (DBA) or trade name of the company" />
                    {displayNameError?.message && (
                      <h6 className="error-msg">{displayNameError.message}</h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <FormControl
                      style={{ width: '100%' }}
                      required
                      error={!!entityTypeError?.message}
                    >
                      <InputLabel>
                        What type of legal form is the organization?
                      </InputLabel>
                      <Select
                        data-testid="brandBasicDetailsFormEntityTypeSelect"
                        fullWidth
                        required
                        value={entityType}
                        onChange={(event) => setEntityType(event.target.value)}
                      >
                        {ENTITY_TYPES.map((entity) => (
                          <MenuItem value={entity.value} key={entity.value}>
                            {entity.value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {entityTypeError?.message && (
                      <h6 className="error-msg">{entityTypeError.message}</h6>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <IntegrationReactSelect
                      required
                      keyName="country"
                      label="Country of Registration"
                      options={localizedCountryOptions}
                      value={selectedCountryOption}
                      error={!!countryError?.message}
                      handleChange={(option: SelectItem) =>
                        setCountry(option.value)
                      }
                    />
                    <Tooltip title="Select the country in which the company is located" />
                    {countryError?.message && (
                      <h6 className="error-msg">{countryError.message}</h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormEinInput"
                      id="ein"
                      inputProps={{ 'aria-label': 'ein' }}
                      fullWidth
                      required={entityType !== EntityTypes.SoleProprietor}
                      disabled={einDisabled}
                      label={
                        einIssuingCountry === 'US'
                          ? 'EIN'
                          : einIssuingCountry
                          ? 'Tax Number/ID'
                          : 'Tax Number/ID/EIN'
                      }
                      value={ein}
                      error={!!einError?.message}
                      onFocus={() => {
                        if (country === 'CA' && !noticeShown) {
                          setModalType(ModalType.Notice);
                          setNoticeShown(true);
                          localStorage.setItem(
                            OPEN_CANADIAN_BRAND_NOTICE,
                            'true'
                          );
                        }
                      }}
                      onChange={(event) => setEin(event.target.value)}
                      onBlur={validateEin}
                    />
                    {einIssuingCountry === 'US' && (
                      <Tooltip title="The Employer Identification Number (EIN) is a nine-digit number assigned by the IRS.  It's used to identify the tax accounts of employers and certain others who have no employees.  The IRS uses the number to identify taxpayers who are required to file varioius business tax returns.  EINs are used by employers, sole proprietors, corporations, partnerships, non-profit organizations, trusts, estates of decendants, government agencies, certain individuals, and other business entities." />
                    )}
                    {einError?.message && (
                      <h6 className="error-msg">{einError.message}</h6>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <IntegrationReactSelect
                      keyName="einIssuingCountry"
                      disabled={einIssuingCountryDisabled}
                      label={
                        einIssuingCountry === 'US'
                          ? 'EIN Issuing Country'
                          : einIssuingCountry
                          ? 'Tax Number/ID Issuing Country'
                          : 'Tax Number/ID/EIN Issuing Country'
                      }
                      options={localizedCountryOptions}
                      value={selectedEinIssuingCountryOption}
                      handleChange={(option: SelectItem) =>
                        setEinIssuingCountry(option.value)
                      }
                    />
                    <Tooltip
                      title={`Please select the country that has issued the ${
                        einIssuingCountry === 'US'
                          ? 'EIN'
                          : einIssuingCountry
                          ? 'Tax Number/ID'
                          : 'Tax Number/ID/EIN'
                      }.`}
                    />
                    {einIssuingCountryError?.message && (
                      <h6 className="error-msg">
                        {einIssuingCountryError.message}
                      </h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item md={6}>
                  <div className="form-group-field">
                    <FormControl
                      data-testid="brandBasicDetailsFormAltBusinessIdTypeForm"
                      style={{ width: '100%' }}
                      disabled={altBusinessIdTypeDisabled}
                    >
                      <InputLabel>{`DUNS or GIIN or LEI Number`}</InputLabel>
                      <Select
                        data-testid="brandBasicDetailsFormAltBusinessIdTypeSelect"
                        fullWidth
                        value={altBusinessIdType}
                        error={!!altBusinessIdError?.message}
                        onChange={(event) =>
                          setAltBusinessIdType(event.target.value)
                        }
                      >
                        <MenuItem value={'DUNS'}>DUNS</MenuItem>
                        <MenuItem value={'GIIN'}>GIIN</MenuItem>
                        <MenuItem value={'LEI'}>LEI</MenuItem>
                      </Select>
                    </FormControl>
                    <Tooltip
                      title={`Please select an "Alternative Business ID". You can provide the DUNS Number, GIIN, or LEI. This is a complementary and optional field to help better identify the Brand. The "DUNS Number" in particular is important in identifying non-US Brands.`}
                    />
                    {altBusinessIdTypeError?.message && (
                      <h6 className="error-msg">
                        {altBusinessIdTypeError.message}
                      </h6>
                    )}
                  </div>
                </Grid>
                <Grid item md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormAltBusinessIdInput"
                      id="altBusinessId"
                      inputProps={{ 'aria-label': 'altBusinessId' }}
                      fullWidth
                      disabled={altBusinessIdDisabled}
                      label={
                        altBusinessIdType
                          ? `${altBusinessIdType} Number`
                          : 'DUNS or GIIN or LEI Number'
                      }
                      value={altBusinessId}
                      error={!!altBusinessIdError?.message}
                      onChange={(event) => setAltBusinessId(event.target.value)}
                      onBlur={validateAltBusinessId}
                    />
                    <Tooltip
                      title={`Please enter the "Alternative Business ID". This is a complementary and optional field to help better identify the Brand.`}
                    />
                    {altBusinessIdError?.message && (
                      <h6 className="error-msg">
                        {altBusinessIdError.message}
                      </h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormStreetInput"
                      id="street"
                      inputProps={{ 'aria-label': 'street' }}
                      fullWidth
                      required
                      disabled={streetDisabled}
                      label="Address/Street"
                      value={street}
                      error={!!streetError?.message}
                      onChange={(event) => setStreet(event.target.value)}
                      onBlur={validateStreet}
                    />
                    {streetError?.message && (
                      <h6 className="error-msg">{streetError.message}</h6>
                    )}
                  </div>
                </Grid>
                <Grid item md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormCityInput"
                      id="city"
                      inputProps={{ 'aria-label': 'city' }}
                      fullWidth
                      required
                      disabled={cityDisabled}
                      label="City"
                      value={city}
                      error={!!cityError?.message}
                      onChange={(event) => setCity(event.target.value)}
                      onBlur={validateCity}
                    />
                    {cityError?.message && (
                      <h6 className="error-msg">{cityError.message}</h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item md={6}>
                  <div className="form-group-field">
                    {country === 'US' ? (
                      <IntegrationReactSelect
                        keyName="state"
                        required
                        disabled={stateDisabled}
                        label="State"
                        options={usStateOptions}
                        value={selectedStateOption}
                        error={!!stateError?.message}
                        handleChange={(option: SelectItem) =>
                          setState(option.value)
                        }
                      />
                    ) : (
                      <TextField
                        data-testid="brandBasicDetailsFormStateInput"
                        id="state"
                        inputProps={{ 'aria-label': 'state' }}
                        fullWidth
                        required
                        disabled={stateDisabled}
                        label="State/Region"
                        value={state}
                        error={!!stateError?.message}
                        onChange={(event) => setState(event.target.value)}
                        onBlur={validateState}
                      />
                    )}
                    {stateError?.message && (
                      <h6 className="error-msg">{stateError.message}</h6>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      error={!!postalCodeError?.message}
                      value={postalCode}
                      id="postalCode"
                      data-testid="brandBasicDetailsFormPostalCodeInput"
                      disabled={postalCodeDisabled}
                      fullWidth
                      required
                      label={
                        country === 'US'
                          ? 'ZIP Code'
                          : country
                          ? 'Postal Code'
                          : 'Postal Code/ZIP Code'
                      }
                      inputProps={{ 'aria-label': 'postalCode' }}
                      onChange={(event) => setPostalCode(event.target.value)}
                      onBlur={validatePostalCode}
                    />
                    {postalCodeError?.message && (
                      <h6 className="error-msg">{postalCodeError.message}</h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  {selectedCountryOption ? (
                    <div className="form-group-field disabled">
                      <TextField
                        data-testid="brandBasicDetailsFormCountryInput"
                        id="country"
                        inputProps={{ 'aria-label': 'country' }}
                        fullWidth
                        disabled
                        label="Country"
                        value={selectedCountryOption.label}
                      />
                      {countryError?.message && (
                        <h6 className="error-msg">{countryError.message}</h6>
                      )}
                    </div>
                  ) : (
                    <div className="form-group-field disabled">
                      <FormControl style={{ opacity: 0.7 }}>
                        <InputLabel>Country</InputLabel>
                        <Select fullWidth disabled />
                      </FormControl>
                    </div>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormWebsiteInput"
                      id="website"
                      inputProps={{ 'aria-label': 'website' }}
                      fullWidth
                      required={
                        entityType === EntityTypes.PubliclyTradedCompany
                      }
                      disabled={websiteDisabled}
                      label="Website/Online Presence"
                      value={website}
                      error={!!websiteError?.message}
                      onChange={(event) => setWebsite(event.target.value)}
                      onBlur={validateWebsite}
                    />
                    <Tooltip title="If opt-ins are collected via website/online presence then please enter the website/online presence here." />
                    {websiteError?.message && (
                      <h6 className="error-msg">{websiteError.message}</h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormStockSymbolInput"
                      id="stockSymbol"
                      inputProps={{ 'aria-label': 'stockSymbol' }}
                      fullWidth
                      required={
                        entityType === EntityTypes.PubliclyTradedCompany
                      }
                      disabled={stockSymbolDisabled}
                      label="Stock Symbol"
                      value={stockSymbol}
                      error={!!stockSymbolError?.message}
                      onChange={(event) => setStockSymbol(event.target.value)}
                      onBlur={validateStockSymbol}
                    />
                    {stockSymbolError?.message && (
                      <h6 className="error-msg">{stockSymbolError.message}</h6>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <IntegrationReactSelect
                      keyName="stockExchange"
                      required={
                        entityType === EntityTypes.PubliclyTradedCompany
                      }
                      disabled={stockExchangeDisabled}
                      label="Stock Exchange"
                      options={stockExchangeOptions}
                      error={!!stockExchangeError?.message}
                      value={selectedStockExchangeOption}
                      handleChange={(option: SelectItem) =>
                        setStockExchange(option.value)
                      }
                    />
                    {stockExchangeError?.message && (
                      <h6 className="error-msg">
                        {stockExchangeError.message}
                      </h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <IntegrationReactSelect
                      required={entityType !== EntityTypes.SoleProprietor}
                      disabled={verticalTypeDisabled}
                      label="Vertical Type"
                      options={verticalTypeOptions}
                      value={selectedVerticalTypeOption}
                      error={!!verticalTypeError?.message}
                      handleChange={(option: SelectItem) =>
                        setVerticalType(option.value)
                      }
                      keyName="vertical"
                    />
                    {verticalTypeError?.message && (
                      <h6 className="error-msg">{verticalTypeError.message}</h6>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormReferenceIdInput"
                      id="referenceId"
                      value={referenceId}
                      error={!!referenceIdError?.message}
                      required={entityType === EntityTypes.SoleProprietor}
                      disabled={referenceIdDisabled}
                      fullWidth
                      label="Reference ID"
                      inputProps={{ 'aria-label': 'referenceId' }}
                      onChange={(event) => setReferenceId(event.target.value)}
                      onBlur={validateReferenceId}
                    />
                    <Tooltip title="The unique number or code used to reference your client inside your organization. This information is mandatory when selecting a Sole Proprietor entity type." />
                    {referenceIdError?.message && (
                      <h6 className="error-msg">{referenceIdError.message}</h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormFirstNameInput"
                      id="firstName"
                      inputProps={{ 'aria-label': 'firstName' }}
                      fullWidth
                      required={entityType === EntityTypes.SoleProprietor}
                      disabled={firstNameDisabled}
                      label="First Name"
                      value={firstName}
                      error={!!firstNameError?.message}
                      onChange={(event) => setFirstName(event.target.value)}
                      onBlur={validateFirstName}
                    />
                    {firstNameError?.message && (
                      <h6 className="error-msg">{firstNameError.message}</h6>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormLastNameInput"
                      id="lastName"
                      value={lastName}
                      error={!!lastNameError?.message}
                      required={entityType === EntityTypes.SoleProprietor}
                      disabled={lastNameDisabled}
                      fullWidth
                      label="Last Name"
                      inputProps={{ 'aria-label': 'lastName' }}
                      onChange={(event) => setLastName(event.target.value)}
                      onBlur={validateLastName}
                    />
                    {lastNameError?.message && (
                      <h6 className="error-msg">{lastNameError.message}</h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <div className="form-group-field">
                    <TextField
                      data-testid="brandBasicDetailsFormMobilePhoneInput"
                      id="mobilePhone"
                      inputProps={{ 'aria-label': 'mobilePhone' }}
                      fullWidth
                      required={entityType === EntityTypes.SoleProprietor}
                      disabled={mobilePhoneDisabled}
                      label="Mobile Phone"
                      value={mobilePhone}
                      error={!!mobilePhoneError?.message}
                      onChange={(event) => setMobilePhone(event.target.value)}
                      onBlur={validateMobilePhone}
                    />
                    <Tooltip title="This field is required for Sole Proprietor brands and will be used to perform OTP which will allow your brand to be moved to Verified status." />
                    {mobilePhoneError?.message && (
                      <h6 className="error-msg">{mobilePhoneError.message}</h6>
                    )}
                  </div>
                </Grid>
              </Grid>
              <CanadianBrandNoticeModal
                open={modalType === ModalType.Notice}
                handleClose={() => setModalType(ModalType.None)}
              />
            </div>
          </div>
        </Grid>
        <Grid item xs={12} className="bottom-blk">
          <ul className="list-inline">
            <li className="form-group-field">
              <input
                type="submit"
                disabled={
                  isSubmitting ||
                  Object.values(errors).some((error) => !!error?.message)
                }
                value="Next"
              />
            </li>
          </ul>
        </Grid>
      </Grid>
    </form>
  );
};

export default BrandDetailsForm;
